import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import type { Building } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'
import { HeatingSystemCard } from './HeatingSystemCard'
import { RegBLLink } from './RegBLLink'
import {
  getAdministrativeEntriesSorted,
  hasMultipleAdministrativeEntries,
} from '../utils/administrativeHelpers'

interface HeatingSystemsSectionProps {
  building: Building
  t: TFunction
  className?: string
}

const InfoBanner = styled.div`
  padding: 12px 16px;
  background: linear-gradient(135deg, #3b82f615 0%, #3b82f608 100%);
  border: 1px solid #3b82f630;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  margin-bottom: 20px;
`

const BannerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: #3b82f6;
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

const BannerText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

const NoDataCard = styled.div`
  padding: 26px;
  text-align: center;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  border: 1px dashed ${({ theme }) => theme.colors.border.light};
`

const NoDataText = styled.p`
  margin: 0 0 12px 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
`

export const HeatingSystemsSection: React.FC<HeatingSystemsSectionProps> = ({
  building,
  t,
  className,
}) => {
  const adminEntries = getAdministrativeEntriesSorted(
    building.buildings_administrative
  )
  const hasMultiple = hasMultipleAdministrativeEntries(
    building.buildings_administrative
  )

  // No administrative data
  if (adminEntries.length === 0) {
    return (
      <NoDataCard className={className}>
        <NoDataText>{t('heating.noData')}</NoDataText>
        {building.egid && (
          <RegBLLink
            egid={building.egid}
            label={t('heating.checkRegBL')}
            variant="button"
          />
        )}
      </NoDataCard>
    )
  }

  return (
    <div className={className}>
      {/* Show info banner if multiple entries */}
      {hasMultiple && (
        <InfoBanner>
          <BannerHeader>
            <Icon name="info" size="small" />
            {t('heating.multipleEntries.title')}
          </BannerHeader>
          <BannerText>
            {t('heating.multipleEntries.description', {
              count: adminEntries.length,
            })}
          </BannerText>
        </InfoBanner>
      )}

      {/* Render all entries */}
      {adminEntries.map((adminData, index) => (
        <HeatingSystemCard
          key={adminData.id}
          adminData={adminData}
          t={t}
          fallbackEGID={building.egid}
          entryNumber={hasMultiple ? index + 1 : undefined}
          totalEntries={hasMultiple ? adminEntries.length : undefined}
          isMostRecent={index === 0 && hasMultiple}
        />
      ))}
    </div>
  )
}
